/**
 * The "is anything stuck" surfaces: the cash proof, and the two delivery queues.
 *
 * The outbox re-drive is the only write here, and it is a money action: a row in
 * that queue is a fact the portal owes another system, so re-delivering it makes
 * funds or books post entries. That is why it takes a stated reason and a step-up
 * code, exactly like executing a payout.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export interface ReconciliationRun {
  id: string;
  concentration_account_id: string;
  account_label: string | null;
  provider: string;
  as_of: string;
  currency_code: string;
  bank_balance: string;
  derived_balance: string;
  difference: string;
  is_baseline: boolean;
  unseen_count: number;
  credits_unexplained: number;
  debits_unexplained: number;
  /** 'clean' | 'drift' | 'baseline' — the daily control's verdict. */
  status: string;
  findings: { kind?: string; [key: string]: unknown }[];
}

export interface DeadLetteredEvent {
  id: string;
  event_id: string;
  event_type: string;
  aggregate_id: string;
  attempts: number;
  last_error: string | null;
  dead_lettered_at: string;
  created_at: string;
}

export interface WebhookDeadLetter {
  id: string;
  provider: string;
  resource_type: string;
  resource_id: string;
  operation_type: string;
  occurred_at: string;
  received_at: string;
  attempts: number;
  last_error: string | null;
  last_attempt_at: string | null;
}

/**
 * What happened to ONE named row. The server reports each outcome rather than
 * failing the whole call: an operator re-driving five events after an outage must
 * not lose the other four because one of them had already gone out.
 */
export type RedriveOutcome =
  | 'redriven'
  | 'not_found'
  | 'already_published'
  | 'not_dead_lettered';

export interface RedriveRowResult {
  id: string;
  outcome: RedriveOutcome;
  /** Present only on a row that was actually re-driven. */
  event_type?: string;
}

export interface RedriveResult {
  redriven: number;
  /** How many rows were named. `redriven` can be lower; `results` says why. */
  requested: number;
  results: RedriveRowResult[];
}

/** How long a credit has gone unclaimed, as the server bands it. */
/** `return_due`: nobody identified it within the owner's limit (90 days, owner 2026-10-31). It
 *  goes back to the account it came from; a deferral does not hold it. */
export type UnclaimedBand = 'fresh' | 'unexplained' | 'escalated' | 'overdue' | 'return_due';

export type ReviewOutcome = 'waiting' | 'escalated' | 'return_decided';

/** One unclaimed bank credit. `amount` is the exact decimal string the API sends: it is
 *  shown as given and never computed with. */
export interface UnclaimedCredit {
  id: string;
  posted_at: string;
  amount: string;
  currency_code: string;
  reference_text: string | null;
  rail: string | null;
  bank_name: string;
  market: string;
  /** `return_due` is the system's proposal of the return, never a person's choice. */
  last_outcome: ReviewOutcome | 'return_due' | null;
  last_reason: string | null;
  last_reviewed_at: string | null;
  /** YYYY-MM-DD; while it is today or later the credit reads as `fresh`. */
  quiet_until: string | null;
  age_days: number;
  band: UnclaimedBand;
  needs_a_manager: boolean;
  return_due?: boolean;
  /** For operations only: the clients' transfer notices that share this credit's sending
   *  account or its exact amount. A hint to look at, never a match. */
  deposit_notices?: UnclaimedNoticeHint[];
}

export interface UnclaimedNoticeHint {
  notice_id: string;
  client_id: string;
  client_name: string;
  product_id: string;
  product_name: string;
  amount: string;
  currency_code: string;
  sent_on: string;
  recorded_by_client: boolean;
  same_account: boolean;
  same_amount: boolean;
}

export interface UnclaimedReviewBody {
  outcome: ReviewOutcome;
  /** 10-2000 characters. */
  reason: string;
  /** 1-30; required for `waiting` and refused for nothing else, so it is sent only there. */
  quiet_days?: number;
}

export interface UnclaimedReviewResult {
  outcome: ReviewOutcome;
  quiet_until: string | null;
}

class OpsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async reconciliationRuns(limit = 60): Promise<ReconciliationRun[]> {
    const res = await this.api.get<ReconciliationRun[]>('/api/v1/ops/bank/reconciliation-runs', {
      params: { limit },
    });
    return res.data;
  }

  async webhookDeadLetters(limit = 100): Promise<WebhookDeadLetter[]> {
    const res = await this.api.get<WebhookDeadLetter[]>('/api/v1/ops/bank/webhook-dead-letters', {
      params: { limit },
    });
    return res.data;
  }

  async outboxDeadLetters(limit = 100): Promise<DeadLetteredEvent[]> {
    const res = await this.api.get<DeadLetteredEvent[]>('/api/v1/ops/outbox/dead-lettered', {
      params: { limit },
    });
    return res.data;
  }

  /** Named rows only — there is no bulk re-drive, by design. */
  async redrive(outboxIds: string[], reason: string, mfaCode: string): Promise<RedriveResult> {
    const res = await this.api.post<RedriveResult>('/api/v1/ops/outbox/redrive', {
      outbox_ids: outboxIds,
      reason,
      mfa_code: mfaCode,
    });
    return res.data;
  }

  /** Credits nobody has claimed, oldest first, each with its band. */
  async unclaimed(limit = 200): Promise<UnclaimedCredit[]> {
    const res = await this.api.get<UnclaimedCredit[]>('/api/v1/ops/bank/unclaimed', {
      params: { limit },
    });
    return res.data;
  }

  /** Record that someone looked, and what they decided. Moves no money. */
  async reviewUnclaimed(
    bankTransactionId: string,
    body: UnclaimedReviewBody,
  ): Promise<UnclaimedReviewResult> {
    const res = await this.api.post<UnclaimedReviewResult>(
      `/api/v1/ops/bank/unclaimed/${bankTransactionId}/review`,
      body,
    );
    return res.data;
  }
}

export default new OpsService();
